import { useStyles } from "../../../theme/appStyles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import {
  useCreateProductMutation,
  useGetMainItemQuery,
  useGetBrandQuery,
  useGetTagQuery,
  useGetPrinterQuery,
  useGetStoreQuery,
  useGetStoreItemByCategoryQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  IBrand,
  IMainItem,
  IPrinter,
  ITag,
  IVariant,
  IStore,
  IStoreItem,
} from "../../../Api/Interface/api.interface";
import LoopingTable from "../Components/productLooping";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { SizeOfLevelType } from "../../../Core/Enum/enum";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import AppsIcon from "@mui/icons-material/Apps";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useNavigate, useParams } from "react-router-dom";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { HexColorPicker } from "react-colorful";
import AddIcon from "@mui/icons-material/Add";
import Switch, { SwitchProps } from "@mui/material/Switch";
import NewProductBrand from "../../Brand/Edit/index";
import NewProductTag from "../../Tag/Edit/index";
import NewPopUpPrinter from "../../Printer/Edit/index";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(28px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 30, // Increased thumb size
    height: 30, // Increased thumb size
  },
  "& .MuiSwitch-track": {
    borderRadius: 34 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#757575" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`, // Using SVG path for check
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`, // Using SVG path for minus
      right: 12,
    },
  },
}));

const steps = ["Basic Details", "Looping Table", "Done"];

const img1 = require("../../../Images/img1.jpg");
const img2 = require("../../../Images/img2.jpg");
const img3 = require("../../../Images/img3.jpg");

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const isMobileRes = useIsMobile();
  const gridHeight = isMobileRes ? "30px" : "400px";
  const { id: MainItemId } = useParams<{ id: string }>();
  const [newProduct, { isLoading }] = useCreateProductMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [storeItem, setStoreItemList] = useState<IStoreItem[]>([]);

  const {
    data: mainItemdata,
    isLoading: mainItemIsLoading,
    isError: mainItemIsError,
  } = useGetMainItemQuery({
    variantBrandId: null,
    categoryId: null,
    variantTagId: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    searchText: "",
    createdDate: null,
    id: String(MainItemId),
  });

  const mainItemListData = useMemo(() => {
    if (
      mainItemdata &&
      Array.isArray(mainItemdata.data) &&
      mainItemdata.data.length > 0
    ) {
      return mainItemdata.data[0] as IMainItem;
    }
    return null;
  }, [mainItemdata]);

  const { data: productBrandData, isLoading: ProductBrandLoading } =
    useGetBrandQuery({
      searchText: "",
      isActive: "1",
      id: "",
    });
  const { data: productTagData, isLoading: ProductTagLoading } = useGetTagQuery(
    {
      searchText: "",
      isActive: "1",
      id: "",
    }
  );
  const { data: printerData, isLoading: PrinterLoading } = useGetPrinterQuery({
    searchText: "",
  });

  const { data: storeData, isLoading: storeIsLoading } = useGetStoreQuery({
    searchText: "",
    id: "",
  });

  const {
    data: itemStoreData,
    isLoading: itemStoreIsLoading,
    isError: itemIsError,
  } = useGetStoreItemByCategoryQuery({
    id: String(mainItemListData?.categoryId),
  });

  const [openGallery, setOpenGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [productDetails, setProductDetails] = useState({
    id: "",
    isLooping: false,
    loopingTableId: "",
    variantName: "",
  });
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [openProductBrand, setOpenProductBrand] = useState(false);
  const [openProductTag, setOpenProductTag] = useState(false);
  const [openPrinter, setOpenPrinter] = useState(false);

  const MainItem = useMemo(() => {
    if (
      mainItemdata &&
      Array.isArray(mainItemdata.data) &&
      mainItemdata.data.length > 0
    ) {
      return mainItemdata.data[0] as IMainItem;
    }
    return null;
  }, [mainItemdata]);

  console.log(MainItem);

  const productBrandList = useMemo(() => {
    return productBrandData?.data as IBrand[];
  }, [productBrandData?.data]);

  const productTagList = useMemo(() => {
    return productTagData?.data as ITag[];
  }, [productTagData?.data]);

  const storeItemList: IStoreItem[] = Array.isArray(itemStoreData?.data)
    ? (itemStoreData?.data as IStoreItem[])
    : [];

  const getAllStoreList = useMemo(() => {
    return storeData?.data as IStore[];
  }, [storeData?.data]);

  useEffect(() => {
    setStoreItemList(storeItemList);
  }, [storeItemList]);

  const getStoreMap = useMemo(() => {
    const map = new Map();
    getAllStoreList?.forEach((category) => {
      map.set(category.id, category.storeName);
    });
    return map;
  }, [getAllStoreList]);

  const isPrinterArray = (data: any): data is IPrinter[] => {
    return (
      Array.isArray(data) &&
      data.every(
        (item) =>
          "id" in item && "printerName" in item && "printerDescription" in item
      )
    );
  };

  const printerList: IPrinter[] = useMemo(() => {
    if (!printerData || !isPrinterArray(printerData.data)) {
      return [];
    }
    return printerData.data;
  }, [printerData]);

  const formik = useFormik<IVariant>({
    initialValues: {
      variantName: "",
      variantRefNumber: "",
      variantDisplayName: "",
      categoryId: "",
      cost: null,
      regularPrice: null,
      salePrice: null,
      isLooping: false,
      loopingTableId: "",
      variantShortDescription: "",
      variantLongDescription: "",
      variantUnit: "",
      isActive: true,
      variantImg: "",
      mainItemId: "",
      packCount: "",
      size: null,
      bgColor: "",
      orderNumber: null,
      isPosDisplay: true,
      isOnSale: true,
      variantBrandId: "",
      variantTagId: [],
      variantPrinterIds: [],
      sizeUnit: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let variantImg: string | undefined = "";

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("variantImg", selectedImage);
          variantImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const temData = {
          variantName: toProperCase(values.variantName),
          variantDisplayName: values.variantDisplayName,
          variantRefNumber: values.variantRefNumber,
          categoryId: MainItem?.categoryId,
          isLooping: MainItem?.mainItemIsLooping,
          loopingTableId: MainItem?.mainItemLoopingConstant,
          cost: values.cost,
          regularPrice: values.regularPrice,
          salePrice: values.salePrice,
          variantUnit: values.variantUnit,
          isActive: values.isActive,
          variantImg,
          mainItemId: String(MainItemId),
          packCount: values.packCount,
          size: values.size,
          bgColor: values.bgColor,
          orderNumber: values.orderNumber,
          isPosDisplay: values.isPosDisplay,
          isOnSale: values.isOnSale,
          variantBrandId: values.variantBrandId,
          variantTagId: values.variantTagId,
          variantPrinterIds: values.variantPrinterIds,
          variantShortDescription: values.variantShortDescription,
          variantLongDescription: values.variantLongDescription,
          storeItemPrice: storeItem,
          sizeUnit: values.sizeUnit,
        };

        const addProductResponse = await newProduct(temData).unwrap();
        if (!addProductResponse.status) {
          showErrorMessage(addProductResponse.message);
        } else {
          showMessage(addProductResponse.message);
          resetForm();
          setSelectedImage(undefined);
          setOpenGallery(false);
          setSelectedColor("");
          setProductDetails({
            id: addProductResponse.data.id,
            isLooping: addProductResponse.data.isLooping,
            loopingTableId: addProductResponse.data.loopingTableId,
            variantName: values.variantName,
          });
          if (addProductResponse.data.isLooping) {
            setActiveStep(1);
          } else {
            setActiveStep(2);
            navigate(`/variant/view/${addProductResponse.data.id}`);
          }
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.variantName === "" ||
      formik.values.variantName === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleGallerySelection = (selectedImage: string) => {
    fetch(selectedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "image.jpg", { type: blob.type });
        setSelectedImage(file);
      });
    setOpenGallery(false);
  };

  const openGalleryDialog = () => {
    setOpenGallery(true);
  };

  const closeGalleryDialog = () => {
    setOpenGallery(false);
  };

  const galleryImages = [img2, img3, img2, img3, img2, img3];

  const handlePrinterToggle = (printerId: number) => {
    const { variantPrinterIds } = formik.values;
    const updatedPrinterIds = variantPrinterIds.includes(printerId)
      ? variantPrinterIds.filter((id) => id !== printerId)
      : [...variantPrinterIds, printerId];
    formik.setFieldValue("variantPrinterIds", updatedPrinterIds);
  };

  const handleColorChange = (newColor: string) => {
    setSelectedColor(newColor);
    formik.setFieldValue("bgColor", newColor);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOkay = () => {
    setActiveStep(0);
    formik.resetForm();
    navigate(`/main/item/view/${MainItemId}`);
  };

  const handleLoopingTableComplete = () => {
    setActiveStep(2);
    navigate(`/main/item/view/${MainItemId}`);
  };

  const handleAdjustmentChange = (
    index: number,
    field: string,
    value: string | number
  ) => {
    setStoreItemList((prevStoreItem) => {
      const updatedItems = [...prevStoreItem];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]:
          field === "referenceNo"
            ? value
            : parseFloat(Number(value).toFixed(2)),
      };
      return updatedItems;
    });
  };

  if (
    mainItemIsLoading ||
    ProductBrandLoading ||
    ProductTagLoading ||
    PrinterLoading
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const cancelFunction = () => {
    formik.resetForm();
    setSelectedImage(undefined);
    setOpenGallery(false);
    setSelectedColor("");
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              className={classes.stepperCardGrid}
              sx={{
                height: { gridHeight },
                p: 2,
              }}
            >
              <Stepper
                activeStep={activeStep}
                orientation={isMobileRes ? "horizontal" : "horizontal"}
                sx={{ height: "100%" }}
              >
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {activeStep === 0 ? (
            <form onSubmit={formik.handleSubmit}>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Add Variant Image
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} p={"24px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Box className={classes.imgBox}>
                          {selectedImage && (
                            <img
                              src={URL.createObjectURL(selectedImage)}
                              alt="Uploaded"
                              className={classes.SelectImageBox}
                            />
                          )}
                          <div>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="icon-button-file-1"
                              type="file"
                              onChange={handleImageChange}
                            />
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <label htmlFor="icon-button-file-1">
                                <IconButton
                                  color="primary"
                                  aria-label="upload picture"
                                  component="span"
                                >
                                  <CloudUploadIcon sx={{ fontSize: 45 }} />
                                </IconButton>
                              </label>
                              <IconButton
                                color="primary"
                                aria-label="select from gallery"
                                component="span"
                                onClick={openGalleryDialog}
                              >
                                <AppsIcon sx={{ fontSize: 45 }} />
                              </IconButton>
                              <div>PNG, JPG and JPEG are allowed</div>
                            </div>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Variant Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Name{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Variant Name"
                              size="small"
                              {...formik.getFieldProps("variantName")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Display Name
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Display Name"
                              size="small"
                              {...formik.getFieldProps("variantDisplayName")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Brand</Typography>
                          </Grid>
                          <Grid item lg={10.5} md={10.5} sm={10.5} xs={10.5}>
                            <TextField
                              select
                              size="small"
                              className={classes.textField}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                              {...formik.getFieldProps("variantBrandId")}
                            >
                              <MenuItem
                                value=""
                                disabled
                                style={{ color: "gray" }}
                              >
                                Select an option
                              </MenuItem>
                              {productBrandList &&
                                productBrandList.map((productBrand) => (
                                  <MenuItem
                                    key={productBrand.id}
                                    value={productBrand.id}
                                  >
                                    {productBrand.brandName}
                                  </MenuItem>
                                ))}
                            </TextField>
                          </Grid>
                          <Grid
                            item
                            lg={1.5}
                            md={1.5}
                            sm={1.5}
                            xs={1.5}
                            display="flex"
                            sx={{
                              justifyContent: "flex-end",
                              alignItems: "flex-end",
                              textAlign: "end",
                            }}
                          >
                            <Button
                              variant="contained"
                              className={classes.editButton}
                              onClick={() => {
                                setOpenProductBrand(true);
                              }}
                            >
                              <AddIcon />
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Tag</Typography>
                          </Grid>
                          <Grid item lg={10.5} md={10.5} sm={10.5} xs={10.5}>
                            <TextField
                              select
                              size="small"
                              sx={{ width: "100%" }}
                              SelectProps={{
                                multiple: true,
                                native: false,
                              }}
                              defaultValue={[]}
                              InputLabelProps={{ shrink: true }}
                              {...formik.getFieldProps("variantTagId")}
                              onChange={(event) => {
                                const {
                                  target: { value },
                                } = event;
                                formik.setFieldValue(
                                  "variantTagId",
                                  typeof value === "string"
                                    ? value.split(",")
                                    : value
                                );
                              }}
                            >
                              {productTagList && productTagList.length > 0 ? (
                                productTagList.map((productTag: ITag) => (
                                  <MenuItem
                                    key={productTag.id}
                                    value={productTag.id}
                                  >
                                    {productTag.tagName}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem
                                  value=""
                                  disabled
                                  style={{ color: "gray" }}
                                >
                                  Select an option
                                </MenuItem>
                              )}
                            </TextField>
                          </Grid>
                          <Grid
                            item
                            lg={1.5}
                            md={1.5}
                            sm={1.5}
                            xs={1.5}
                            display="flex"
                            sx={{
                              justifyContent: "flex-end",
                              alignItems: "flex-end",
                              textAlign: "end",
                            }}
                          >
                            <Button
                              variant="contained"
                              className={classes.editButton}
                              onClick={() => {
                                setOpenProductTag(true);
                              }}
                            >
                              <AddIcon />
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Cost</Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Cost"
                              size="small"
                              type="number"
                              {...formik.getFieldProps("cost")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Regular Price
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Regular Price"
                              size="small"
                              type="number"
                              {...formik.getFieldProps("regularPrice")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Sale Price
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Sale Price"
                              size="small"
                              type="number"
                              {...formik.getFieldProps("salePrice")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Conversion unit
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              select
                              size="small"
                              className={classes.textField}
                              SelectProps={{
                                native: false,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                              {...formik.getFieldProps("variantUnit")}
                            >
                              <MenuItem value="" disabled>
                                Select some option
                              </MenuItem>

                              {Object.entries(SizeOfLevelType).map(
                                ([key, value], index) => (
                                  <MenuItem key={index} value={value}>
                                    {key}
                                  </MenuItem>
                                )
                              )}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Pack Count
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Pack Count"
                              size="small"
                              {...formik.getFieldProps("packCount")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Size</Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter Size"
                              size="small"
                              type="number"
                              {...formik.getFieldProps("size")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Size Conversion unit
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              select
                              size="small"
                              className={classes.textField}
                              SelectProps={{
                                native: false,
                              }}
                              defaultValue=""
                              InputLabelProps={{ shrink: true }}
                              {...formik.getFieldProps("sizeUnit")}
                            >
                              <MenuItem value="" disabled>
                                Select some option
                              </MenuItem>

                              {Object.entries(SizeOfLevelType).map(
                                ([key, value], index) => (
                                  <MenuItem key={index} value={value}>
                                    {key}
                                  </MenuItem>
                                )
                              )}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Order Number
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Order Number"
                              size="small"
                              {...formik.getFieldProps("orderNumber")}
                              type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Reference Number{" "}
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Reference Number"
                              size="small"
                              {...formik.getFieldProps("variantRefNumber")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container spacing={2}>
                          <Grid item lg={4} md={4} sm={4} xs={6}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Active
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomSwitch
                                  color="primary"
                                  sx={{ mr: 2 }}
                                  formik={formik}
                                  name="isActive"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={4} md={4} sm={4} xs={6}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Sale
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomSwitch
                                  color="primary"
                                  sx={{ mr: 2 }}
                                  formik={formik}
                                  name="isOnSale"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item lg={4} md={4} sm={4} xs={6}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Display
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <CustomSwitch
                                  color="primary"
                                  sx={{ mr: 2 }}
                                  formik={formik}
                                  name="isPosDisplay"
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Button Color
                            </Typography>
                          </Grid>

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Box className={classes.colorBox}>
                              <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                onClick={toggleColorPicker}
                              >
                                {/* Left Side - Color Preview Box */}
                                <Grid item display="flex" alignItems="center">
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      backgroundColor: selectedColor,
                                      border: "1px solid #d3d3d3",
                                      borderRadius: "8px",
                                    }}
                                  />
                                </Grid>

                                {/* Right Side - Color Picker Icon */}
                                <Grid item>
                                  <IconButton
                                    color="primary"
                                    component="span"
                                    onClick={toggleColorPicker}
                                  >
                                    <ColorLensIcon sx={{ fontSize: 45 }} />
                                  </IconButton>
                                </Grid>
                              </Grid>

                              {showColorPicker && (
                                <Box
                                  mt={2}
                                  display="flex"
                                  justifyContent="center"
                                >
                                  <HexColorPicker
                                    color={selectedColor}
                                    onChange={handleColorChange}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Printer</Typography>
                          </Grid>
                          <Grid item lg={10.5} md={10.5} sm={10.5} xs={10.5}>
                            <Box className={classes.printerBox}>
                              <Grid container spacing={1} p={2}>
                                {printerList.map((printer) => (
                                  <Grid
                                    item
                                    lg={4}
                                    md={4}
                                    sm={6}
                                    xs={6}
                                    key={printer.id}
                                  >
                                    <Typography variant="subtitle1">
                                      {printer.printerName}
                                    </Typography>
                                    <IOSSwitch
                                      color="primary"
                                      sx={{ mr: 2 }}
                                      checked={formik.values.variantPrinterIds.includes(
                                        printer.id
                                      )}
                                      onChange={() =>
                                        handlePrinterToggle(printer.id)
                                      }
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Grid>
                          <Grid
                            item
                            lg={1.5}
                            md={1.5}
                            sm={1.5}
                            xs={1.5}
                            display="flex"
                            sx={{
                              justifyContent: "flex-end",
                              alignItems: "flex-end",
                              textAlign: "end",
                            }}
                          >
                            <Button
                              variant="contained"
                              className={classes.editButton}
                              onClick={() => {
                                setOpenPrinter(true);
                              }}
                            >
                              <AddIcon />
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Short Description
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Short Description"
                              size="small"
                              multiline
                              rows={3}
                              {...formik.getFieldProps(
                                "variantShortDescription"
                              )}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Long Description
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Long Description .."
                              size="small"
                              multiline
                              rows={8}
                              {...formik.getFieldProps(
                                "variantLongDescription"
                              )}
                              className={classes.discriptionTextField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TableContainer>
                          <Table className={classes.table}>
                            <TableHead className={classes.tableHeaderRow}>
                              <TableRow>
                                <TableCell className={classes.tablheader}>
                                  <strong>Supplier</strong>
                                </TableCell>
                                <TableCell className={classes.tablheader}>
                                  <strong>Reference No</strong>
                                </TableCell>
                                <TableCell className={classes.tablheader}>
                                  <strong>Price</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {itemStoreIsLoading ? (
                                <TableRow>
                                  <TableCell colSpan={20} align="center">
                                    <Box
                                      display="flex"
                                      justifyContent="center"
                                      alignItems="center"
                                      height="10vh"
                                    >
                                      <CircularProgress />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ) : itemIsError ? (
                                <TableRow>
                                  <TableCell colSpan={20} align="center">
                                    {"Some thing went to wrong"}
                                  </TableCell>
                                </TableRow>
                              ) : storeItemList.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={20} align="center">
                                    {"No store available"}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                storeItem.map((row: IStoreItem, index) => (
                                  <TableRow
                                    key={index}
                                    className={classes.tableRowHover}
                                  >
                                    <TableCell
                                      scope="row"
                                      className={classes.tablCell}
                                    >
                                      {getStoreMap.get(row.storeId)}
                                    </TableCell>
                                    <TableCell
                                      scope="row"
                                      className={classes.tablCell}
                                    >
                                      <TextField
                                        placeholder="Reference No"
                                        size="small"
                                        value={row.referenceNo}
                                        onChange={(e) =>
                                          handleAdjustmentChange(
                                            index,
                                            "referenceNo",
                                            e.target.value
                                          )
                                        }
                                        className={classes.textField}
                                        InputProps={{
                                          sx: {
                                            fontSize: 14,
                                            border: "none",
                                          },
                                          classes: {
                                            root: classes.textFieldQuoteNoBorder,
                                            notchedOutline:
                                              classes.notchedOutline,
                                          },
                                        }}
                                        InputLabelProps={{
                                          sx: {
                                            fontSize: 14,
                                            border: "none",
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      scope="row"
                                      className={classes.tablCell}
                                    >
                                      <TextField
                                        placeholder="Amount"
                                        size="small"
                                        value={row.price}
                                        onChange={(e) =>
                                          handleAdjustmentChange(
                                            index,
                                            "price",
                                            parseFloat(e.target.value)
                                          )
                                        }
                                        type="number"
                                        className={classes.textField}
                                        InputProps={{
                                          sx: {
                                            fontSize: 14,
                                            border: "none",
                                          },
                                          classes: {
                                            root: classes.textFieldQuoteNoBorder,
                                            notchedOutline:
                                              classes.notchedOutline,
                                          },
                                        }}
                                        InputLabelProps={{
                                          sx: {
                                            fontSize: 14,
                                            border: "none",
                                          },
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={cancelFunction}
                        >
                          Reset
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={
                            isLoading ? (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                              />
                            ) : (
                              <CheckCircleOutlineIcon />
                            )
                          }
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          Create
                        </Button>
                      </Grid>
                    </Grid>

                    {openProductBrand && (
                      <NewProductBrand
                        handleCloseDialog={() => setOpenProductBrand(false)}
                        openModel={openProductBrand}
                      />
                    )}

                    {openProductTag && (
                      <NewProductTag
                        handleCloseDialog={() => setOpenProductTag(false)}
                        openModel={openProductTag}
                      />
                    )}

                    {openPrinter && (
                      <NewPopUpPrinter
                        handleCloseDialog={() => setOpenPrinter(false)}
                        openModel={openPrinter}
                      />
                    )}
                  </Card>
                </Grid>
              </Grid>
              <Dialog open={openGallery} onClose={closeGalleryDialog}>
                <Grid
                  className={
                    isMobileRes ? classes.DialogMobile : classes.Dialog
                  }
                >
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Select an Image from Gallery
                      </Typography>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <ImageList
                      sx={{ width: 500, height: 450 }}
                      cols={3}
                      rowHeight={164}
                    >
                      {galleryImages.map((imagePath) => (
                        <ImageListItem key={imagePath}>
                          <img
                            src={`${imagePath}?w=164&h=164&fit=crop&auto=format`}
                            srcSet={`${imagePath}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                            alt="Gallery"
                            loading="lazy"
                            onClick={() => handleGallerySelection(imagePath)}
                            style={{ cursor: "pointer" }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </DialogContent>
                </Grid>
              </Dialog>
            </form>
          ) : activeStep === 1 ? (
            <LoopingTable
              variantName={productDetails.variantName}
              productId={productDetails.id}
              isLooping={productDetails.isLooping}
              loopingTableId={productDetails.loopingTableId}
              loopingTableComplete={handleLoopingTableComplete}
            />
          ) : (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      py={2}
                      alignContent={"center"}
                      textAlign={"center"}
                    >
                      <Typography className={classes.cardTitle} p={10}>
                        Product Created Successfully
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<TaskAltIcon />}
                        className={classes.addButton}
                        onClick={handleOkay}
                        disabled={isLoading}
                      >
                        Done
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
